import {BaseElement, OSkinableInit} from "back/commons/basis";
import {JCidMetas, resPath} from "lib/store/res";
import {IReg, REG} from "lib/commons/registry";
import {IDepotResUiEnv, IDepotUiEnv} from "lib/store/depot";
import {Button} from "back/commons/widgets/buttons";
import {DOMSH} from "lib/commons/xml/domsh";
import {JNodeInfos, JNodeShortProps, URLTREE} from "lib/store/urlTree";
import {JSX} from "lib/commons/xml/dom";
import {SelectRes, SelectResBrowseArea} from "back/store/dialogs/selectRes";
import {VIEWS} from "lib/commons/views";
import {EditMetasRes} from "back/store/dialogs/editMetasRes";
import {ICloseable} from "back/commons/widgets/popupable";
import {POPUP} from "back/commons/widgets/popups";
import {EditPrcIdent, OEditPrcIdentInit} from "back/store/dialogs/editPrcIdent";


/**
 * Widget de gestion d'un processus d'upload CID.
 * Enchaine les 1, 2 ou 3 écrans : SelectRes, AddRes, EditMetas
 * Ce dialogue n'exécute pas le push cid mais retourne les paramètres Cid.
 */
export interface UploadRes extends BaseElement {
	initialize(init: OUploadResInit): this
}

export interface OUploadResInit extends OSkinableInit {

	reg: IReg<IDepotUiEnv>

	/** Session Cid en cours. */
	scCidSessId: string


	/** Path de la ressource cible imposé. Si spécifié, le target path n'est pas modifiable dans le processus */
	targetPath?: resPath

	/** Processing imposé. Si spécifié, le processing n'est pas modifiable dans le processus
	 * Pour **proposer** un processing, déclarer l'entrée 'processing' dans `otherProps` */
	processing?: string

	/**
	 * L'upload concerne l'accueil d'un dossier et non une ressource terminale.
	 * Note : affecté automatiquement à l'init si le processing est connu et est de type folder,
	 * Utilisé pour :
	 * - réduire la lite des processing à sélectionner,
	 * - adapter la sémantique des messages aux users.
	 */
	uploadFolder?: boolean

	/**
	 * Cas particuier d'un "faux upload" : création d'une ressource (de type folder ou pas) qui est "NoContent", mais avec des metas.
	 * Note : affecté automatiquement à l'init si le processing est connu avec sa prop ResType.prcNoContent,
	 * Utilisé pour :
	 * - réduire la lite des processing à sélectionner,
	 * - adapter la sémantique des messages aux users (si besoin).
	 */
	uploadNoContent?: boolean

	/** Autres metas (que targetPath et processing) à passer à l'édition des métas et à retrouner si non modifiées. */
	otherProps: Dict<string>

	/**
	 * Dossier imposé dans lequel ajouter la resource (!= de rootFolderPath qui n'imposerait qu'une racine).
	 * Utilisé si targetPath non renseigné.
	 */
	fixedFolderPath?: resPath

	/**
	 * Dossier par défaut dans lequel propose l'ajout de la resource.
	 * Utilisé si targetPath non renseigné.
	 */
	defaultFolderPath?: resPath

	/** Nom par défaut en cas d'ajout dans un dossier. */
	initialFileName?: string

	/** Contexte d'appel auprès duquel retourner le résultat. */
	holder?: ICloseable<JCidMetas | null>
}

export class UploadRes extends BaseElement {

	reg: IReg<IDepotUiEnv>;

	config: OUploadResInit;

	result: JCidMetas;

	get holder(): ICloseable<JCidMetas | null> {return this.config.holder || POPUP.findPopupableParent(this)}

	protected _initialize(init: OUploadResInit) {
		this.reg = this.findReg(init);
		this.result = Object.create(init.otherProps || null);
		if (init.processing) {
			const prc = this.reg.env.resTypes.getResTypeByPrc(init.processing);
			if (prc === this.reg.env.resTypes.unknownType) {
				//anomalie : prc inconnu, on va le demander au user.
				init = Object.create(init);
				init.processing = null;
			} else {
				if ((prc.prcIsFolder !== !!init.uploadFolder) || (prc.prcIsNoContent !== !!init.uploadNoContent)) {
					//ajustement auto des configs uploadFolder et uploadNoContent.
					init = Object.create(init);
					init.uploadFolder = prc.prcIsFolder;
					init.uploadNoContent = prc.prcIsNoContent;
				}
			}
		}
		this.config = init;
		this.attachShadow(DOMSH.SHADOWDOM_INIT);
		this._initAndInstallSkin(this.localName, init);
		if (!init.targetPath && !init.fixedFolderPath) {
			//Détermination de la cible ou de son dossier.
			this.selectPath();
		} else {
			//Path ou folder fixé, on saute la step 1
			this.selectPrcFileName();
		}
		// this.addEventListener("keydown", (ev:KeyboardEvent)=>{
		// 	if (ev.key === "Escape") this.cancel();
		// });
	}

	/**
	 * Step 1
	 * Si pas de initPath ou de fixedFolderPath imposés, on propose une interface de sélection du path cible à remplacer ou du dossier parent.
	 */
	selectPath() {
		const me = this;
		const msg = this.config.uploadFolder
			? "Sélectionnez le dossier dans lequel ajouter un sous-dossier ou le dossier à mettre à jour"
			: "Sélectionnez un dossier dans lequel ajouter le contenu ou la ressource à mettre à jour";
		this.shadowRoot.appendChild(<c-msg label={msg} level="info"/>);
		const selRes = this.shadowRoot.appendChild(new SelectRes().initialize({
			reg: this.reg,
			initialPath: this.config.defaultFolderPath || URLTREE.DEFAULT_PATH_ROOT,
			boards: [new SelectResBrowseArea()],
			selBtnLabel: "Ajouter...",
			checkSel: (selectRes: SelectRes & { updateFolderBtn?: Button }) => {
				const resProps = selectRes.currentSel;
				if (this.config.uploadFolder) {
					if (!selectRes.updateFolderBtn) {
						//1er check ajout du bouton
						selectRes.updateFolderBtn = new Button().initialize({
							reg: this.reg,
							label: "Mettre à jour...",
							title: "Mettre à jour l'accueil de ce dossier",
							uiContext: "dialog"
						})
						selectRes.updateFolderBtn.onclick = () => {
							this.result.path = URLTREE.extractUnversionedLeafPath(selectRes.currentSel.permaPath);
							this.selectPrcFileName();
						}
						selectRes.doBtn.title = "Ajouter un sous-dossier à ce dossier";
						selectRes.doBtn.parentElement.insertBefore(selectRes.updateFolderBtn, selectRes.doBtn);
					}
					if (!resProps || !this.isFolder(resProps)) {
						//On n'est pas sur un dossier
						selectRes.updateFolderBtn.disabled = true;
						return msg;
					}
					//On est sur un dossier
					selectRes.updateFolderBtn.disabled = false;
				} else {
					if (!resProps) return msg;
					if (this.isFolder(resProps)) {
						//On est sur un dossier
						selectRes.doBtn.label = "Ajouter...";
						selectRes.doBtn.title = "Ajouter dans ce dossier";
					} else {
						selectRes.doBtn.label = "Remplacer...";
						selectRes.doBtn.title = "Remplacer cette ressource avec le nouveau contenu";
					}
				}
				return true;
			},
			holder: {
				close(resProps) {
					if (resProps) {
						if (me.isFolder(resProps) && !me.result.path) {
							//On a sélectionné le dossier cible et pas encore le path de la res à ajouter
							me.config.fixedFolderPath = resProps.permaPath;
						} else {
							//On a sélectionné la res à remplacer.
							const type = me.reg.env.resTypes.getResTypeFor(resProps);
							if (type.prcIsFolder === !!me.config.uploadFolder) {
								//On est sur un type de res de même nature folder/leaf, on va reprendre les valeurs précédentes par défaut
								//On reprend les metas actuelles pour constituer les valurs par défaut par défaut
								if((resProps as JNodeInfos).metas) Object.assign(me.result, (resProps as JNodeInfos).metas);
								//On verrouille les props structurantes.
								me.result.processing = resProps.prc;
								me.result.olderResId = resProps.resId; //on préserve la généalogie pour reprise des metas.
							}
							me.result.path = URLTREE.extractUnversionedLeafPath(resProps.permaPath);
						}
						me.selectPrcFileName();
					} else {
						me.cancel();
					}
					return true;
				}
			}
		}));
		VIEWS.onViewShown(selRes);
	}

	/** Step 2 : fixe le path et le processing si nécessaire. */
	selectPrcFileName() {
		const me = this;
		const knownTargetPath: string | null = this.result.path ?? this.config.targetPath;
		if (knownTargetPath != null && this.config.processing) {
			//Prc et Path établis, on finalise les metas.
			me.result.path = knownTargetPath;
			me.result.processing = this.config.processing;
			this.editMetas();
			return;
		}
		//path ou prc non définitif
		VIEWS.clearContent(this.shadowRoot, true);
		const prc = this.config.processing ? this.reg.env.resTypes.getResTypeByPrc(this.config.processing) : null;
		const editPrcIdent = this.shadowRoot.appendChild(new EditPrcIdent().initialize({
			reg: this.reg,
			folderPath: this.config.fixedFolderPath || URLTREE.extractParentPath(knownTargetPath) || this.config.defaultFolderPath || URLTREE.DEFAULT_PATH_ROOT,
			folderUi: knownTargetPath != null || this.config.fixedFolderPath != null ? "readOnly" : "editable",
			leafName: knownTargetPath != null ? URLTREE.extractLeafName(knownTargetPath) : this.config.initialFileName || null,
			leafUi: knownTargetPath != null ? URLTREE.isRootPath(knownTargetPath) ? "hidden" : "readOnly" : "editable",
			resUpdatable: 'always',
			otherMetasIn: this.result,
			resTypesTree: {
				resTypes: prc ? [prc] : this.reg.env.resTypes.getResTypesTree(
					(resType) => resType.prcIsFolder === !!this.config.uploadFolder && resType.prcIsNoContent === !!this.config.uploadNoContent
				)
			},
			defaultPrc: this.result.processing,
			holder: {
				close(result?: JCidMetas | null): boolean {
					if (result) {
						Object.assign(me.result, result);
						//if (result.olderResId != null) me.result.olderResId = ""; //controle de concurrence lâche si pas de métas, sera reprécisé si metas.
						me.editMetas();
					} else {
						me.cancel();
					}
					return true;
				}
			}
		} as OEditPrcIdentInit));
		VIEWS.onViewShown(editPrcIdent);
	}

	/** Step 3 : finalisation des metas. */
	async editMetas() {
		const me = this;
		const resType = this.reg.env.resTypes.getResTypeByPrc(this.result.processing);
		const editMetasCode = resType.reg.getPref("editMetas.beforeUpload", "editMetas");
		if (!editMetasCode || !resType.resForm(editMetasCode)) {
			//Pas de métas à éditer, Fin.
			this.finish();
			return;
		}
		let reg: IReg<IDepotResUiEnv>;
		try {
			//Construction du reg dédié au resType cible.
			reg = this.reg.env.universe.newDepotResUiRegInCreation(this.reg, this.result.path, resType);
			if (this.result.olderResId != null) {
				//On a un contenu existant (sauf si chgt de version à l'édition de l'identité).
				const fullNodeInfos = await this.reg.env.universe.adminUrlTree.nodeInfos(this.result.path);
				if (fullNodeInfos) {
					//Affectation de la version exacte utilisée pour initialiser les metas pour le controle d'édition concurrente.
					reg.env.nodeInfos = fullNodeInfos;
					me.result.olderResId = fullNodeInfos.resId;
				} else {
					//On reset ce older plus valide (version probablement modifiée).
					this.result.olderResId = undefined;
				}
			}

			// A voir si utile... surcharge par des metas venant des paramètres entrant du process CID
			// let metas = reg.env.nodeInfos.metas;
			// if(!metas) metas = reg.env.nodeInfos.metas = {};
			// Object.assign(metas, this.config.metasIn);

			VIEWS.clearContent(this.shadowRoot, true);
			const editMetas = this.shadowRoot.appendChild(new EditMetasRes().initialize({
				reg,
				defaultValues: this.result,
				holder: {
					close(cidMetas?: JCidMetas | null): boolean {
						reg.close();
						if (cidMetas) {
							Object.assign(me.result, cidMetas);
							me.finish();
						} else {
							me.cancel();
						}
						return true;
					}
				}
			}));
			VIEWS.onViewShown(editMetas);
		} catch (e) {
			this.cancel();
			reg.close();
			throw e;
		}
	}

	/** Abandon CID */
	cancel() {
		this.holder?.close(null);
	}

	/** Finalisation CID */
	finish() {
		//console.log("finish CIDinteraction:::", this.result);
		this.holder?.close(this.result);
	}

	isFolder(resProps: JNodeShortProps) {
		return resProps.t === "home" || resProps.t === "noContent";
	}
}

REG.reg.registerSkin('store-upload-res', 1, /* language=CSS */ `
	:host {
		flex: 1;
		display: flex;
		min-width: 0;
		min-height: 0;
		flex-direction: column;
	}

	c-msg {
		flex: 0 0 auto;
		padding: .5em;
		border-bottom: 1px solid var(--border-color);
	}

	store-add-res {
		width: unset;
		max-width: unset;
	}
`);

customElements.define("store-upload-res", UploadRes);
